import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 60000,
  // Send the access-gate cookie on every request so the backend gate
  // can verify entry without the frontend ever reading the value.
  withCredentials: true,
});

// ── Access gate ────────────────────────────────────────────────────────
export const authStatus = () => api.get("/auth/status").then((r) => r.data);
export const authVerify = (code) =>
  api.post("/auth/verify", { code }).then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);

// Conversations & chat
export const listConversations = () => api.get("/chat/conversations").then((r) => r.data);
export const createConversation = (title) =>
  api.post("/chat/conversations", { title }).then((r) => r.data);
export const renameConversation = (id, title) =>
  api.patch(`/chat/conversations/${id}`, { title }).then((r) => r.data);
export const deleteConversation = (id) =>
  api.delete(`/chat/conversations/${id}`).then((r) => r.data);
export const getHistory = (id) => api.get(`/chat/history/${id}`).then((r) => r.data);
export const sendChat = (payload) => api.post("/chat", payload).then((r) => r.data);

/**
 * Regenerate a single step's icon on an existing assistant message.
 * Used by the per-card "Regenerate" button when an icon didn't quite
 * fit. The backend reuses the original `image_prompt` stored in the
 * message's metadata so the regeneration is consistent in style.
 *
 * Returns: { step_index, image_b64, ok }
 */
export const regenerateStepImage = (messageId, stepIndex) =>
  api
    .post(`/chat/messages/${messageId}/regenerate-image`, { step_index: stepIndex }, {
      timeout: 90000, // image gen can take up to ~60s + retry
    })
    .then((r) => r.data);

/**
 * Stream a chat. handlers: { onMeta, onToken, onReminderCreated, onReminderLookup, onDone, onError }
 * Returns an AbortController for cancellation.
 */
export function streamChat(payload, handlers) {
  const controller = new AbortController();
  (async () => {
    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        handlers.onError?.(new Error(`HTTP ${resp.status}`));
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const block = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          let event = "message";
          let data = "";
          for (const line of block.split("\n")) {
            if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("data:")) data += line.slice(5).trim();
          }
          if (!data) continue;
          let parsed = null;
          try { parsed = JSON.parse(data); } catch { continue; }
          // V9.44.3 — Per-event try/catch so a handler throw (e.g. a
          // React state setter rejecting an unexpected payload shape)
          // doesn't break the entire stream loop and leave the chat
          // in a half-rendered state. Errors surface to onError so
          // the UI can recover gracefully.
          try {
            if (event === "meta") handlers.onMeta?.(parsed);
            else if (event === "token") handlers.onToken?.(parsed);
            else if (event === "reminder_created") handlers.onReminderCreated?.(parsed);
            else if (event === "task_delegated") handlers.onTaskDelegated?.(parsed);
            else if (event === "visual_sent_to_contact") handlers.onVisualSentToContact?.(parsed);
            else if (event === "step_guide_start") handlers.onStepGuideStart?.(parsed);
            else if (event === "step_guide_status") handlers.onStepGuideStatus?.(parsed);
            else if (event === "step_image") handlers.onStepImage?.(parsed);
            else if (event === "step_guide") handlers.onStepGuide?.(parsed);
            else if (event === "show_face") handlers.onShowFace?.(parsed);
            else if (event === "realtime_result") handlers.onRealtimeResult?.(parsed);
            else if (event === "reminder_lookup") handlers.onReminderLookup?.(parsed);
            else if (event === "sms_sent") handlers.onSmsSent?.(parsed);
            else if (event === "done") handlers.onDone?.(parsed);
            else if (event === "error") handlers.onError?.(new Error(parsed.message || "stream error"));
          } catch (handlerErr) {
            // eslint-disable-next-line no-console
            console.error(`[chat-stream] handler for '${event}' threw:`, handlerErr);
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") handlers.onError?.(e);
    }
  })();
  return controller;
}

// Memory
export const listMemory = (params = {}) =>
  api.get("/memory", { params }).then((r) => r.data);
export const listMemoryTags = () => api.get("/memory/tags").then((r) => r.data);
export const createMemory = (content, tags = [], source = "manual") =>
  api.post("/memory", { content, tags, source }).then((r) => r.data);
export const deleteMemory = (id) => api.delete(`/memory/${id}`).then((r) => r.data);
export const updateMemory = (id, payload) =>
  api.patch(`/memory/${id}`, payload).then((r) => r.data);

// Reminders
export const listReminders = () => api.get("/reminders").then((r) => r.data);
export const createReminder = (payload) =>
  api.post("/reminders", payload).then((r) => r.data);
export const updateReminder = (id, payload) =>
  api.patch(`/reminders/${id}`, payload).then((r) => r.data);
export const deleteReminder = (id) =>
  api.delete(`/reminders/${id}`).then((r) => r.data);
export const checkDueReminders = () =>
  api.get("/reminders/check").then((r) => r.data);
export const acknowledgeReminder = (id) =>
  api.post(`/reminders/${id}/acknowledge`).then((r) => r.data);
export const snoozeReminder = (id, minutes = 10) =>
  api.post(`/reminders/${id}/snooze`, null, { params: { minutes } }).then((r) => r.data);
export const pauseReminder = (id) =>
  api.post(`/reminders/${id}/pause`).then((r) => r.data);
export const resumeReminder = (id) =>
  api.post(`/reminders/${id}/resume`).then((r) => r.data);
export const skipNextReminder = (id) =>
  api.post(`/reminders/${id}/skip-next`).then((r) => r.data);

// Suggestions
export const generateSuggestions = (conversation_id) =>
  api.post("/suggestions/generate", { conversation_id }).then((r) => r.data);
export const inlineSuggestion = (conversation_id, message_id) =>
  api.post("/suggestions/inline", { conversation_id, message_id }).then((r) => r.data);
export const listSuggestions = (status = "pending") =>
  api.get("/suggestions", { params: { status } }).then((r) => r.data);
export const acceptSuggestion = (id, payload = {}) =>
  api.post(`/suggestions/${id}/accept`, payload).then((r) => r.data);
export const dismissSuggestion = (id) =>
  api.post(`/suggestions/${id}/dismiss`).then((r) => r.data);
export const ignoreSuggestion = (id) =>
  api.post(`/suggestions/${id}/ignore`).then((r) => r.data);

// Search
export const globalSearch = (q, limit = 8) =>
  api.get("/search", { params: { q, limit } }).then((r) => r.data);

// Files
export const uploadFile = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};
export const downloadFileUrl = (id) => `${API}/files/${id}/download`;
export const deleteFile = (id) => api.delete(`/files/${id}`).then((r) => r.data);

// Settings
export const getSettings = () => api.get("/settings").then((r) => r.data);
export const updateSettings = (payload) =>
  api.put("/settings", payload).then((r) => r.data);

// V11 — schedule changes made by contacts over SMS
export const listScheduleActivity = (limit = 50) =>
  api.get("/schedule-activity", { params: { limit } }).then((r) => r.data);

// Contacts
export const listContacts = () => api.get("/contacts").then((r) => r.data);
export const createContact = (payload) =>
  api.post("/contacts", payload).then((r) => r.data);
export const updateContact = (id, payload) =>
  api.patch(`/contacts/${id}`, payload).then((r) => r.data);
export const deleteContact = (id) =>
  api.delete(`/contacts/${id}`).then((r) => r.data);

// Tasks
export const listTasks = (status) =>
  api.get("/tasks", { params: status ? { status } : {} }).then((r) => r.data);
export const createTask = (payload) =>
  api.post("/tasks", payload).then((r) => r.data);
export const updateTask = (id, payload) =>
  api.patch(`/tasks/${id}`, payload).then((r) => r.data);
export const deleteTask = (id) =>
  api.delete(`/tasks/${id}`).then((r) => r.data);
export const completeTask = (id) =>
  api.post(`/tasks/${id}/complete`).then((r) => r.data);
export const followupTask = (id, hours = 24) =>
  api.post(`/tasks/${id}/followup`, null, { params: { hours } }).then((r) => r.data);
export const listTaskMessages = (id) =>
  api.get(`/tasks/${id}/messages`).then((r) => r.data);
export const queueTaskMessage = (id, content) =>
  api.post(`/tasks/${id}/messages`, { content }).then((r) => r.data);
export const draftTaskMessage = (id, intent = "") =>
  api.post(`/tasks/${id}/draft`, { intent }).then((r) => r.data);
export const approveSendTaskMessage = (tid, mid) =>
  api.post(`/tasks/${tid}/messages/${mid}/approve_send`).then((r) => r.data);
export const rejectTaskMessage = (tid, mid) =>
  api.post(`/tasks/${tid}/messages/${mid}/reject`).then((r) => r.data);
export const taskTransportStatus = () =>
  api.get("/tasks/transport/status").then((r) => r.data);

// V9.1 — Autonomous task agent
export const startTaskAgent = (id, dryRun = true) =>
  api.post(`/tasks/${id}/start`, { dry_run: dryRun }).then((r) => r.data);
export const simulateInbound = (id, content, dryRun = true) =>
  api.post(`/tasks/${id}/inbound`, { content, dry_run: dryRun }).then((r) => r.data);
export const setTaskStatus = (id, status) =>
  api.post(`/tasks/${id}/manual_status`, { status }).then((r) => r.data);

// V9.4 — Web Push diagnostics (restored)
export const pushStatus = () => api.get("/push/status").then((r) => r.data);
export const pushTestDispatch = () =>
  api.post("/push/test-dispatch").then((r) => r.data);
